import { Inject, Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { NOTIFIER, Notifier } from '@shared/notifier/types';
import { PlatformAppContext } from '@/app/platform/platform-context/platform-app-context';
import { USER_QUERIES, UserQueries } from '@/app/platform/platform-core/user/store/types';

/**
 * Programmatic navigeren naar een EgiPage op basis van zijn page-config-id.
 *
 * Een kale `router.navigate(['/human-design/…'])` werkt in dit framework niet:
 * elke pagina leeft onder een `:appId`-segment en de {@link AuthorizationGuard}
 * leest de rechten uit het navigatie-item via de `navId`-queryparam. Deze
 * service zoekt het navigatie-item van de doelpagina in de menuboom van de
 * gebruiker (zoals het paginamenu zelf doet) en navigeert met de juiste
 * routerLink én navId. Vereist dus dat de pagina in het menu geconfigureerd is
 * en dat de gebruiker er rechten op heeft; anders volgt een nette melding.
 */
@Injectable({ providedIn: 'root' })
export class EgiPageNavigationService {
  constructor(
    private readonly router: Router,
    @Inject(USER_QUERIES)
    private readonly userQueries: UserQueries,
    @Inject(NOTIFIER)
    private readonly notifier: Notifier,
    private readonly appContext: PlatformAppContext,
  ) {}

  /**
   * Navigeert naar een clickthrough-pagina van de ACTIEVE pagina (het
   * `clickTroughPages`-blok in haar route-config): het bijbehorende
   * child-navigatie-item draagt de navId, en de breadcrumb biedt dan de
   * terugknop naar de vertrekpagina.
   * @returns false wanneer de clickthrough niet geconfigureerd is of de
   * gebruiker er geen rechten op heeft — de caller kiest dan een fallback
   * (bv. {@link navigateToPage} naar het gewone menu-item).
   */
  navigateToClickTroughPage(clickTroughName: string, extraQueryParams?: Params): Promise<boolean> | false {
    const navItem = this.appContext.activeNavigationItem;
    const clickTroughId = navItem?.egiPage?.config?.clickTroughPages?.[clickTroughName]?.id;
    if (!clickTroughId) {
      return false;
    }
    const child = navItem.children.find(candidate =>
      candidate.hasEgiPage
      && candidate.egiPage.config.id === clickTroughId
      && candidate.isBaseAccessAllowed);
    if (!child?.routerLink) {
      return false;
    }
    return this.router.navigate([child.routerLink], {
      queryParams: { navId: child.id, ...extraQueryParams },
    });
  }

  /**
   * Navigeert naar de pagina met het gegeven page-config-id (de `config.id`
   * uit de route-registratie), met extra queryparams naast de vereiste navId.
   * @returns false wanneer de pagina niet in het menu van de gebruiker staat.
   */
  navigateToPage(pageConfigId: string, extraQueryParams?: Params): Promise<boolean> | false {
    for (const app of this.userQueries.getAvailableApps() ?? []) {
      const item = app.rootNavigationItem
        ?.clone()
        .asArray.find(candidate =>
          candidate.hasEgiPage
          && candidate.egiPage.config.id === pageConfigId
          && candidate.isBaseAccessAllowed);
      if (item?.routerLink) {
        return this.router.navigate([item.routerLink], {
          queryParams: { navId: item.id, ...extraQueryParams },
        });
      }
    }
    this.notifier.warning('Deze pagina staat niet in je menu of je hebt er geen rechten op.');
    return false;
  }
}
